<script setup lang="ts">
import type { ContentPageProps } from '@nimbus/types'
import { isEmpty } from 'es-toolkit/compat'
import type { UIIconUnion } from '@core/types'
import type { Game, GameClickEventEmit, GameTilePosition, GameTrackingOrigin } from '@game/types'
import type { BingoTrustpilotReviewProps } from './TrustpilotReview.vue'
import type { BingoInfoProps } from './Info.vue'
import type { ImageOptions as NuxtImageOptions, ConfiguredImageProviders } from '@nuxt/image'
import { useUserInfo, useBingo, useContentSeo, useHead, useSeoMeta, computed, ref, useGames, useGame, useGameList, useGameListTracking, useI18n, useImage, useGameAssets, useDocumentVisibility, watch, type BingoRoundInformation, GameListSlug, GameTileType, mediaUrl, useTemplateRef, useDeferredVideoAutoplay } from '#imports'

// @nuxt/image's signature hardcodes the default provider; widen it to opt into 'cloudflare'.
type BingoImageOptions = Omit<NuxtImageOptions, 'provider'> & { provider?: keyof ConfiguredImageProviders }

export interface BingoLobbyProps extends ContentPageProps {
    bingo: {
        slug: keyof BingoRoundInformation
        featured?: {
            slug: string
            showInGameList?: boolean
        }
        headerTitle: string
        prizePoolText: string
    }
    background?: {
        color?: string
    }
    logo?: {
        src: string
    }
    stats: {
        megaPrize: string | undefined
        nextRound: string
        video: {
            src: string
            poster: string
        }
    }
    winners: {
        title: string
    }
    sheets: {
        title: string
        subtitle: string
        image: {
            src: string
            alt?: string
        }
    }
    facts: {
        items: string[]
        cta: {
            button: {
                text: string
                icon?: UIIconUnion
            }
        }
        review?: BingoTrustpilotReviewProps
    }
    games: {
        title: string
    }
    info: {
        intro: {
            title: string
            subtitle: string
            image: {
                src: string
                alt: string
                width?: string | number
                height?: string | number
            }
        }
        howTo: {
            title: string
            items: BingoInfoProps['howToItems']
        }
    }
}
const props = defineProps<BingoLobbyProps>()

const ORIGIN: GameTrackingOrigin = 'bingo-lobby'

const { getLatestBingoWinners, getNextRound, megaPrize, getPrizePool, display, setupListeners } = useBingo()
const { t } = useI18n()

// Facts
const { start, getGameBySlug } = useGame()
const { data: featuredGame } = getGameBySlug(props.bingo?.featured?.slug || '', { lazy: true })

const exclusiveGamesSlugs = ['bingo', 'bingo-90', props.bingo?.featured?.showInGameList ? props.bingo.featured.slug : '']

// Data & SEO
const [
    { isExisting },
    { data: pageData },
    latestBingoWinners,
    { refresh: refreshNextRound },
    { refresh: refreshNextRound90 },
    { data: bingoGames },
    { data: exclusiveBingoGames }
] = await Promise.all([
    useUserInfo(),
    { data: ref(props.page) },
    getLatestBingoWinners(),
    getNextRound('bingo'),
    getNextRound('bingo-90'),
    useGames({
        filter: {
            slugs: ['bingo', 'bingo-90']
        },
        key: 'bingo-games',
        lazy: true
    }),
    useGames({
        filter: {
            slugs: exclusiveGamesSlugs
        },
        key: 'exclusive-bingo-games',
        lazy: true
    })
])

const { getHead, getSeoMeta } = useContentSeo(
    pageData.value?.seo || { ...(props.seo.defaults || {}) }
)
const emit = defineEmits<GameClickEventEmit>()

// One tracking scope per slug: the exclusive-games list, and the two standalone
// tile groups whose clicks must not land on the list's slug.
const { trackingListSlug, trackingOrigin, onGameClick: trackListClick } = useGameListTracking(ORIGIN, GameListSlug.BINGO_LOBBY)
const { onGameClick: trackFavouriteClick } = useGameListTracking(ORIGIN, GameListSlug.BINGO_FAVOURITES)
const { onGameClick: trackFeaturedClick } = useGameListTracking(ORIGIN, GameListSlug.BINGO_FEATURED)

function onListGameClick(game: Game, position?: GameTilePosition) {
    trackListClick(game, position)

    emit('game-click', game, position)
}

function onFavouriteGameClick(game: Game) {
    trackFavouriteClick(game)

    emit('game-click', game)
}

function onFeaturedGameClick(game: Game) {
    trackFeaturedClick(game)

    emit('game-click', game)
}

useHead(getHead())
useSeoMeta(getSeoMeta())
// ./ Data & SEO

// Provides list-scoped jackpot/promotion queries and a shared opening-hours
// clock for the direct <GameTile> elements in this lobby (bingoGames + featured).
// Nested <GameList> usages (e.g. exclusive games) set up their own inner scopes.
useGameList(() => {
    const games = [...(bingoGames.value?.data ?? [])]
    if (featuredGame.value) games.push(featuredGame.value)
    return games
})

const img = useImage() as (src: string, modifiers?: NuxtImageOptions['modifiers'], options?: BingoImageOptions) => string

const { mediaCdnUrl } = useGameAssets()
const gameplayVideoSrc = computed(() => mediaCdnUrl(props.stats.video.src, 360))
const videoEl = useTemplateRef<HTMLVideoElement>('videoEl')
useDeferredVideoAutoplay(videoEl)
const bingoLogoBgUrl = computed(() => {
    if (!props.logo?.src) return 'none'

    const imgUrl = img(props.logo.src, { width: 260 }, { preset: 'local', provider: 'cloudflare' })

    return `url('${imgUrl}')`
})

const headerBackground = computed(() => props.background?.color
    ? { '--bingo-header-bg': props.background.color, '--bingo-header-bg-ink': '#fff' }
    : {})

function startBingo() {
    start({ gameSlug: props.bingo.slug })
}
// ./ Facts

if (import.meta.client) {
    setupListeners()

    const visibility = useDocumentVisibility()
    watch(visibility, (current, previous) => {
        if (current === 'visible' && previous === 'hidden') {
            refreshNextRound()
            refreshNextRound90()
        }
    })
}
</script>

<template>
    <div
        class="bingo"
        :class="{
            'bingo--has-reviewer': props.facts.review,
            'bingo--90': bingo.slug === 'bingo-90'
        }"
        :style="headerBackground"
    >
        <div class="bingo__wrap bingo__wrap--top">
            <header class="bingo__box">
                <!-- eslint-disable-next-line vue/no-v-html props.bingo is safe -->
                <h1 v-if="props.bingo.headerTitle" class="bingo__headline" v-html="props.bingo.headerTitle" />
                <div class="bingo__favorite-game">
                    <template v-if="bingoGames?.data?.length">
                        <div
                            v-for="(game) in bingoGames.data"
                            :key="game.slug"
                        >
                            <GameTile
                                :game="game"
                                :video-enabled="false"
                                :data-test="`bingo-container-element-game-tile bingo-container-element-game-tile-${game.slug}`"
                                @game-click="onFavouriteGameClick"
                            />

                            <div class="bingo__favorite-game_prizepool">
                                <b>{{ props.bingo.prizePoolText }}</b>

                                <div class="bingo__favorite-game_prizepool-value">
                                    <em v-if="getPrizePool(game.slug as keyof BingoRoundInformation)">
                                        <UIAnimatedNumber :value="getPrizePool(game.slug as keyof BingoRoundInformation)" />
                                    </em>

                                    <UILoader
                                        v-else
                                        :inline="true"
                                        :color="false"
                                        text=""
                                        subtle="true"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <div>
                            <div class="skeleton-item" />
                            <div class="bingo__favorite-game_prizepool">
                                <b>{{ props.bingo.prizePoolText }}</b>
                                <div class="bingo__favorite-game_prizepool-value">
                                    <UILoader
                                        :inline="true"
                                        :color="false"
                                        text=""
                                        subtle="true"
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div class="skeleton-item" />
                            <div class="bingo__favorite-game_prizepool">
                                <b>{{ props.bingo.prizePoolText }}</b>
                                <div class="bingo__favorite-game_prizepool-value">
                                    <UILoader
                                        :inline="true"
                                        :color="false"
                                        text=""
                                        subtle="true"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>

                    <div class="bingo__stats">
                        <div v-if="props.stats?.megaPrize" class="bingo__stat">
                            <!-- eslint-disable-next-line vue/no-v-html props.stats is safe -->
                            <h3 v-html="props.stats.megaPrize" />

                            <em v-if="megaPrize">
                                <UIAnimatedNumber
                                    :value="megaPrize"
                                />
                            </em>
                        </div>

                        <div class="bingo__stat">
                            <!-- eslint-disable-next-line vue/no-v-html props.stats is safe -->
                            <h3 v-html="props.stats.nextRound" />
                            <em>{{ display[props.bingo.slug] || t('game.bingoPage.soon') }}</em>
                        </div>
                    </div>
                </div>

                <figure class="bingo__balls bingo__balls--top">
                    <NuxtImg
                        class="bingo__ball--green"
                        preset="local"
                        provider="cloudflare"
                        :src="mediaUrl('/media/bingo/ball-green.png')"
                        densities="1x 2x"
                        alt="Bingo ball green"
                        width="40"
                        height="40"
                        quality="70"
                    />

                    <NuxtImg
                        class="bingo__ball--purple"
                        preset="local"
                        provider="cloudflare"
                        :src="mediaUrl('/media/bingo/ball-purple.png')"
                        densities="1x 2x"
                        alt="Bingo ball purple"
                        width="68"
                        height="68"
                        quality="70"
                    />
                </figure>
            </header>
            <div v-if="props.bingo?.featured?.slug" class="bingo__game-tile-video">
                <GameTile
                    v-if="!isEmpty(featuredGame)"
                    :game="featuredGame"
                    :video-enabled="true"
                    :enforce-video-type="GameTileType.VIDEO"
                    @game-click="onFeaturedGameClick"
                />
            </div>
            <ClientOnly>
                <Transition
                    name="fade-up"
                    appear
                >
                    <div class="bingo__phone bingo__phone--top">
                        <div class="bingo__video bingo__video--gameplay">
                            <video
                                ref="videoEl"
                                loop
                                muted
                                playsinline
                                disable-remote-playback
                                loading="lazy"
                                decoding="async"
                                :src="gameplayVideoSrc"
                                :poster="img(props.stats.video.poster, { width: 360, quality: 48 }, { preset: 'local', provider: 'cloudflare' })"
                            />
                        </div>
                    </div>
                </Transition>
            </ClientOnly>

            <!-- eslint-disable-next-line vue/no-v-html props.winners is safe -->
            <h3 class="bingo-winners__head" v-html="props.winners.title" />
            <NimbusBingoWinners :bingo-winners="latestBingoWinners.bingoWinners.value" />

            <NimbusBingoSheets v-if="megaPrize">
                <div>
                    <!-- eslint-disable-next-line vue/no-v-html props.sheets is safe -->
                    <h2 v-html="props.sheets.title" />
                    <!-- eslint-disable-next-line vue/no-v-html props.sheets is safe -->
                    <p v-html="props.sheets.subtitle" />
                </div>
                <NuxtImg
                    preset="local"
                    provider="cloudflare"
                    class="bingo__sheets"
                    decoding="async"
                    loading="lazy"
                    :src="props.sheets.image.src"
                    :alt="props.sheets.image?.alt"
                    densities="1x 2x"
                    width="576"
                    height="382"
                    quality="70"
                />
            </NimbusBingoSheets>

            <div class="bingo__facts-wrap">
                <NimbusBingoFacts :items="props.facts.items">
                    <template #cta>
                        <UIButton
                            id="bingo-cta-2"
                            class="bingo__btn"
                            size="xl"
                            as="cta"
                            :color="props.bingo.slug === 'bingo-90' ? '#3ca641' : undefined"
                            @click.prevent="startBingo"
                        >
                            {{ props.facts.cta.button.text }}

                            <template v-if="props.facts.cta.button.icon" #append>
                                <UIIcon :name="props.facts.cta.button.icon" size="16" />
                            </template>
                        </UIButton>
                    </template>
                </NimbusBingoFacts>

                <NimbusBingoTrustpilotReview v-if="props.facts.review" v-bind="props.facts.review" />
            </div>
        </div>

        <div class="bingo__wrap bingo__wrap--middle bingo__wrap--small-padding bingo__games">
            <GameList
                category="BINGO"
                :tracking-origin="trackingOrigin"
                :tracking-list-slug="trackingListSlug"
                :title="props.games.title"
                :games="exclusiveBingoGames?.data"
                :video-enabled="false"
                :skeletons="exclusiveGamesSlugs.length"
                :has-sidebar="true"
                @game-click="onListGameClick"
            />
        </div>

        <div class="bingo__wrap">
            <NimbusBingoInfo :how-to-title="props.info.howTo.title" :how-to-items="props.info.howTo.items">
                <template #intro>
                    <NuxtImg
                        class="bingo-info__intro-image"
                        preset="local"
                        provider="cloudflare"
                        loading="lazy"
                        decoding="async"
                        :src="props.info.intro.image.src"
                        :alt="props.info.intro.image.alt"
                        :width="props.info.intro.image.width || 300"
                        :height="props.info.intro.image.height || 181"
                        quality="70"
                    />

                    <div class="bingo-info__subwelcome">
                        <!-- eslint-disable-next-line vue/no-v-html props.intro is safe -->
                        <h2 v-html="props.info.intro.title" />
                        <!-- eslint-disable-next-line vue/no-v-html props.intro is safe -->
                        <p v-html="props.info.intro.subtitle" />
                    </div>
                </template>
            </NimbusBingoInfo>
        </div>

        <div
            v-if="!isExisting && !isEmpty(pageData?.dynamicContent)"
            class="bingo__wrap bingo__wrap--bottom"
        >
            <LazyNimbusContentDynamicRenderer
                :content="pageData?.dynamicContent"
                :max-width="1024"
            />
        </div>
    </div>
</template>

<style lang="scss">
@function pixel-to-vw($pixels, $designWidth: 1920) {
    @return math.div($pixels, $designWidth) * 100vw;
}

.bingo {
    position: relative;
    z-index: 2;
    line-height: 1.2;
    overflow: hidden;
    width: calc(100% + 30px);
    margin-left: -15px;

    @media (min-width: $screen-md) {
        width: calc(100% + 60px);
        margin-left: -30px;
    }

    @media (min-width: $screen-lg) {
        border-radius: var(--border-radius, $border-radius);
        width: 100%;
        margin-left: 0;
    }

    @media (min-width: #{$screen-xl + 20}) {
        border-radius: var(--border-radius, $border-radius);
    }

    h1,
    h2,
    h3 {
        // Temporary fix to Nuxt v-html render bug, sometimes placing a <b>-tag inside headlines
        &,
        & b {
            font-weight: $bingo-headline-weight;
        }
    }

    h1 {
        margin-top: 0;
    }

    h2 {
        margin: 70px 0 15px;
        font-size: pixel-to-vw(26, 360);

        @media (min-width: $screen-md) {
            font-size: 36px;
        }
    }

    h3 {
        font-size: pixel-to-vw(16, 360);

        @media (min-width: $screen-md) {
            font-size: 18px;
        }
    }

    h4 {
        font-size: pixel-to-vw(12, 360);

        @media (min-width: $screen-md) {
            font-size: 12px;
        }
    }

    em {
        color: $bingo-yellow;
        font-style: normal;
    }

    .game-list__title {
        margin: 70px 0 15px;
        font-size: pixel-to-vw(26, 360);
        font-family: $heading-font-family;

        @media (min-width: $screen-md) {
            font-size: 36px;
        }
    }

    &__wrap {
        padding: 20px 15px 100px;

        @media (min-width: $screen-md) {
            padding: 20px 30px 100px;
        }

        @media (min-width: $screen-xl) {
            padding: 20px 100px 155px;
        }

        &--small-padding {
            padding: 40px 30px 30px;

            @media (min-width: $screen-md) {
                padding: 20px 30px 30px;
            }

            @media (min-width: $screen-xl) {
                padding: 20px 100px 50px;
            }
        }

        &--top {
            --sticky-terms-color: $white;

            position: relative;
            z-index: 1;
            padding-bottom: 70px;
            color: var(--bingo-header-bg-ink, #fff);
            min-height: calc(100vh - 90px);
            background: var(--bingo-header-bg, none);

            @media (min-width: $screen-lg) {
                border-radius: 0;
            }

            a:not(.btn) {
                &,
                &:hover,
                &:focus {
                    color: #fff;
                }
            }
        }

        &--middle {
            background-color: $bingo-middle-bg-color;
            color: $bingo-middle-color;

            @media (min-width: $screen-lg) {
                border-radius: 0 0 var(--border-radius, $border-radius) var(--border-radius, $border-radius);
            }
        }

        &--bottom {
            background-color: $bingo-bottom-bg-color;
        }

        &--middle {
            a:not(.btn) {
                &,
                &:hover,
                &:focus {
                    color: var(--bingo-header-bg-ink, #{$bingo-purple});
                }
            }
        }

        &--bottom {
            a:not(.btn) {
                &,
                &:hover,
                &:focus {
                    color: $bingo-purple;
                }
            }
        }
    }

    &__box {
        position: relative;
        margin: 20px 0 15px;
        padding: 15px; // Hack (part 1) to make room for textAfterLogo when user isExisting
        border-radius: var(--border-radius-lg, $border-radius-lg);
        min-height: 265px;
        background: $bingo-box-bg-color;

        @media (min-width: $screen-xs) {
            min-height: 280px;
        }

        @media (min-width: $screen-sm) {
            min-height: 305px;
        }

        @media (min-width: $screen-md) {
            margin: 7vh 20px;
            max-width: 46vw;
            min-height: 345px;
            padding: 20px;
        }

        @media (min-width: $screen-lg) {
            margin: 7vh 0;
            width: 510px;
            min-height: 365px;
        }

        // This is to make sure terms is visible on first load
        @media (min-width: $screen-lg) and (min-height: 710px) {
            margin: 75px 0;
        }
    }

    &__headline {
        display: flex;
        gap: 5px;
        place-items: center;
        justify-content: center;
        position: relative;
        margin: 0 0 10px;
        font-size: pixel-to-vw(21, 360);
        line-height: 1;
        text-align: center;
        z-index: 1;
        text-shadow: 0 1px 1px rgb(0 0 0 / 50%);

        @media (min-width: $screen-sm) {
            font-size: pixel-to-vw(25, 414);
        }

        @media (min-width: $screen-md) {
            font-size: pixel-to-vw(22, 768);
        }

        @media (min-width: $screen-lg) {
            font-size: 35px;
        }

        em {
            display: inline-flex;
            flex-grow: 1;
            width: 94px;
            height: 1.1em;
            background-image: v-bind(bingoLogoBgUrl);
            background-size: contain;
            background-position: left top;
            background-repeat: no-repeat;
            color: transparent !important; // to hide bingo text
            object-fit: cover;
            text-shadow: none !important;
            transform: translateY(-2px);
            transition: all 0.3s ease-out;

            @media (min-width: $screen-sm) {
                width: 108px;
            }

            @media (min-width: 600px) {
                width: 124px;
            }

            @media (min-width: $screen-md) {
                width: 108px;
                height: 30px;
            }

            @media (min-width: $screen-lg) {
                width: 130px;
                height: 38px;
            }
        }
    }

    &__headline,
    &-info__cta {
        // bingo-info__cta added here to avoid duplicate style
        // Elements inserted from BO
        b,
        span {
            display: block;
            text-align: center;
        }

        b.highlight {
            color: $bingo-yellow;
        }

        span {
            margin: 5px 0 20px;
            font-size: pixel-to-vw(16, 360);
            font-weight: 600;

            @media (min-width: $screen-md) {
                font-size: pixel-to-vw(20, 768);
            }

            @media (min-width: $screen-lg) {
                font-size: 20px;
            }
        }
    }

    &__btn {
        width: calc(100% - 15px); // Hack (part 2) to make room for textAfterLogo when user isExisting
        font-size: pixel-to-vw(18, 360);

        @media (min-width: $screen-sm) {
            font-size: 19px;
        }
    }

    &__balls {
        margin: 0;

        &,
        img {
            position: absolute;
        }

        &--top {
            top: -30px;
            right: 0;
            z-index: 0;

            @media (min-width: $screen-lg) {
                top: -45px;
            }
        }

        &--bottom {
            bottom: 20px;
            left: 68%;
            z-index: -1;

            .bingo--has-reviewer & {
                bottom: -30px;
                left: 68%;
            }

            @media (min-width: $screen-md) {
                bottom: 120px;
                left: 110%;

                .bingo--has-reviewer & {
                    bottom: 120px;
                    left: 110%;
                }
            }

            .bingo--has-reviewer & {
                left: 70%;

                @media (min-width: $screen-md) {
                    left: 110%;
                }

                @media (min-width: $screen-lg) {
                    bottom: -75px;
                    left: 0;
                }
            }
        }
    }

    &__ball {
        &--green {
            right: 45px;

            @media (min-width: $screen-lg) {
                right: 7px;
                width: 64px;
                height: 64px;
            }
        }

        &--purple {
            right: 0;

            @media (min-width: $screen-lg) {
                right: -80px;
                width: 104px;
                height: 104px;
            }
        }

        &--blue {
            @media (min-width: $screen-md) {
                left: 70px;
                width: 156px;
                height: 156px;
            }

            @media (min-width: $screen-lg) {
                width: 220px;
                height: 220px;
            }
        }

        &--yellow {
            left: -30px;
            top: 60px;

            .bingo--has-reviewer & {
                top: 80px;
                left: 0;
            }

            @media (min-width: $screen-md) {
                height: 60px;
                top: 150px;
                width: 60px;

                .bingo--has-reviewer & {
                    top: 150px;
                    width: 60px;
                    height: 60px;
                }
            }

            @media (min-width: $screen-lg) {
                height: 110px;
                top: 250px;
                width: 110px;

                .bingo--has-reviewer & {
                    top: 250px;
                    width: 110px;
                    height: 110px;
                }
            }

            .bingo--has-reviewer & {
                @media (max-width: #{ $screen-md - 1 }) {
                    top: 100px;
                }
            }
        }
    }

    &__phone {
        padding-top: 15px;
        text-align: center;
        perspective: 800px;

        @media (min-width: $screen-md) {
            padding-top: 30px;
        }

        @media (min-width: $screen-lg) {
            margin: 0 auto;
        }

        &--top {
            position: absolute;
            right: -10px;
            z-index: 0;

            @media (min-width: 375px) {
                right: 0;
            }

            @media (min-width: $screen-md) {
                right: 30px;
                top: 30px;
            }

            @media (min-width: $screen-lg) {
                right: 30px;
                margin: 0;
            }

            @media (min-width: $screen-xl) {
                right: 200px;
            }
        }
    }

    &__video {
        padding: 30px 8px;
        border-radius: 30px;

        @media (min-width: $screen-md) {
            padding: 44px 10px;
            border-radius: 45px;
        }

        video {
            width: auto;
            height: 355px;
            border-radius: 30px;

            @media (min-width: $screen-xs) {
                height: 426px;
            }

            @media (min-width: $screen-md) {
                height: 569px;
                border-radius: 35px;
            }

            @media (min-width: $screen-lg) {
                height: 640px;
                border-radius: 40px;
            }
        }

        &::after {
            content: '';
            position: absolute;
            top: 15px;
            left: 50%;
            width: 25%;
            height: 4px;
            transform: translateX(-50%);
            border-radius: 3px;
            opacity: 0.6;
            background-color: color.adjust($bingo-purple, $lightness: -5%);

            .bingo--90 & {
                background-color: color.adjust($banko-red, $lightness: -5%);
            }

            @media (min-width: $screen-md) {
                top: 24px;
                height: 5px;
            }
        }

        &--gameplay {
            transform: rotateY(-35deg) rotateX(8deg);
            background-color: color.adjust($bingo-purple, $lightness: -18%);
            mask-image: linear-gradient(to top, transparent, transparent 20%, black 55%, black);

            @media (min-width: $screen-md) {
                border-radius: 45px;
                mask-image: linear-gradient(to top, transparent, transparent 15%, black 30%, black);
                transform: rotateY(-35deg) rotateX(10deg);
            }

            .bingo--90 & {
                background-color: color.adjust($banko-red, $lightness: -10%);
            }
        }
    }

    &__stat {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;

        flex: 1;

        @media (min-width: $screen-sm) {
            min-width: 157px;
        }

        h3 {
            margin: 0;
            white-space: nowrap;
        }

        em {
            color: $bingo-yellow;
            font-size: pixel-to-vw(24, 360);
            font-weight: $text-heavy;
            font-style: normal;
            white-space: nowrap;

            @media (min-width: $screen-md) {
                font-size: pixel-to-vw(28, 768);
            }

            @media (min-width: $screen-lg) {
                font-size: 32px;
            }

            .bingo__next-round & {
                display: inline-block;
                min-width: 55px;
                margin-left: 5px;
                font-size: pixel-to-vw(16, 360);

                @media (min-width: $screen-md) {
                    min-width: 82px;
                    font-size: pixel-to-vw(22, 768);
                }

                @media (min-width: $screen-lg) {
                    font-size: 28px;
                }
            }
        }

        .loader {
            margin-left: 0;
        }
    }

    &__stats {
        grid-column: 1 / -1;

        display: flex;
        background-color: rgba(0, 0, 0, 0.2);

        border-radius: var(--border-radius, $border-radius);

        min-height: 44px;
        padding: 4px 8px;

        @media (min-width: $screen-md) {
            justify-content: center;
            width: 57%;
            gap: 30px;
            transform: none;
        }

        @media (min-width: $screen-lg) {
            width: 510px;
        }

        .bingo:not(.bingo--is-existing) & {
            margin-top: 10px;

            @media (min-width: $screen-md) and (min-height: 710px) {
                margin-top: 20px;
            }

            @media (max-width: #{ $screen-md - 1 }) {
                justify-content: space-evenly;
                width: 100%;
            }
        }
    }

    &__game-tile-video {
        position: relative;
        margin: 20px 0;
        width: 100%;
        max-width: 510px;
        min-height: calc(50vw - 40px);

        @include tablet(md) {
            margin: 30px 0;
            max-width: 46vw;
            min-height: calc(23vw - 15px);
        }

        @include desktop {
            width: 510px;
            min-height: 220px;
        }

        &:empty {
            background-color: rgb(255 255 255 / 10%);
            border-radius: var(--game-tile-border-radius, $game-tile-border-radius);
        }
    }

    &__next-round {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin: 0 15px 15px 0;

        b {
            display: inline-block;
            font-size: pixel-to-vw(12, 360);
            font-weight: $text-bold;

            @media (min-width: $screen-md) {
                font-size: pixel-to-vw(16, 768);
            }

            @media (min-width: $screen-lg) {
                font-size: 16px;
            }
        }
    }

    &__konfetti {
        position: absolute;
        right: 0;
        bottom: -55px;
        mask-image: linear-gradient(to bottom, black 50%, transparent);
        pointer-events: none;

        @media (min-width: $screen-xs) {
            right: 18%;
        }

        @media (min-width: $screen-md) {
            right: -10px;
            bottom: -30px;
        }

        @media (min-width: $screen-xl) {
            right: 8%;
            width: 468px;
            height: auto;
        }
    }

    &__terms {
        position: relative;
        z-index: 1;
        // Make sure terms overlap content within ancestor padding
        margin: 10px -15px;
        padding: 12px 15px;

        // Temporary fix - to fix missing 'bottom' style on sticky-terms
        &.offer-terms--sticky {
            bottom: -118px;
        }
    }

    &__games {
        h2 {
            margin-top: 35px;

            @media (max-width: #{ $screen-md - 1 }) {
                text-align: center;
            }
        }
    }

    &__facts-wrap {
        @media (min-width: $screen-lg) {
            min-height: 370px;

            .bingo--has-reviewer & {
                display: flex;
                align-items: flex-start;
                justify-content: space-around;
                min-height: 640px;
                padding-bottom: 65px;
            }
        }
    }

    &__favorite-game {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr min-content;
        gap: 20px;

        > div {
            width: 100%;
        }

        h3 {
            white-space: nowrap;
        }

        em {
            display: inline-block;
            min-width: 55px;
            color: $bingo-yellow;
            font-weight: $text-heavy;
            font-style: normal;
            font-size: 22px;

            @media (min-width: $screen-lg) {
                font-size: 20px;
                min-width: 82px;
            }
        }

        .loader {
            min-width: 65px;
            margin-left: 10px;
        }

        .skeleton-item {
            position: relative;
            background-color: $skeleton-color;
            border-radius: var(--game-tile-border-radius, $game-tile-border-radius);
            overflow: hidden;
            aspect-ratio: 1;
            z-index: 1;
        }

        &_prizepool {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin: 10px 0 0;
            font-size: 18px;
        }

        &_prizepool-value {
            --color: #{$bingo-yellow};
            display: flex;
            align-items: center;
            justify-content: center;
            height: 30px;

            .loader {
                transform: none;
            }
        }
    }
}

.fade-in,
.fade-in-delayed {
    &-enter-from {
        opacity: 0;
    }

    &-enter-active {
        transition: all 1.6s $easeOutQuart;
    }

    &-enter-to {
        opacity: 1;
    }
}

.fade-up {
    &-enter-from {
        transform: translate3d(0, 100px, 0);
        opacity: 0;
    }

    &-enter-active {
        transition: all 1.2s $easeOutQuart 0.8s;
    }

    &-enter-to {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
}
.fade-in-delayed {
    &-enter-active {
        transition-delay: 0.8s;
    }
}
</style>
